import { useEffect, useMemo, useState } from 'react';
import { adresseApi } from '../api/client';
import { stockApi } from '../api/resources';
import { COTE_MAX, reduitLImage } from '../lib/image';
import { TAILLE_MAX_PIECE, enMo, estEnregistre, separeLesPieces } from '../lib/medias';
import { C } from '../theme/palette';

const CELLULE = {
  width: 64,
  height: 64,
  borderRadius: 11,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  boxSizing: 'border-box',
};

const CROIX = {
  position: 'absolute',
  top: 3,
  right: 3,
  width: 18,
  height: 18,
  borderRadius: 99,
  background: 'rgba(15,22,38,.6)',
  color: '#fff',
  border: 'none',
  fontSize: 10,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  padding: 0,
};

/**
 * Photos et plans d'un article de stock, présentés comme la maquette V288 :
 * un bouton « 📎 Ajouter une photo ou un plan », puis des vignettes de 64 px
 * légendées, avec les cases « ＋ Photo » et « ＋ Plan ».
 *
 * Deux carrelages beiges de format voisin ne se distinguent pas par leur
 * référence : la photo est ce qui les sépare en rayon. Le plan du fournisseur
 * se range au même endroit — c'est une pièce jointe à l'article — et le
 * séparer obligerait à tenir deux mécanismes pour une seule intention.
 *
 * Les fichiers ne passent pas par la fiche : ils partent un par un, et la
 * fiche n'en garde que l'adresse. Un article encore en saisie n'a pas
 * d'identifiant à qui les rattacher : avec `onAttente`, les fichiers choisis
 * attendent dans `enAttente`, et l'écran les dépose une fois l'article créé.
 *
 * @param {object} props propriétés du composant
 * @param {number} [props.articleId] article concerné
 * @param {object[]} [props.medias] pièces déjà attachées
 * @param {(medias: object[]) => void} [props.onChange] appelé après chaque
 *   dépôt ou retrait, avec la liste à jour
 * @param {{fichier: Blob, typePiece: string, nom: string}[]} [props.enAttente]
 *   fichiers retenus pour un article pas encore enregistré
 * @param {(liste: object[]) => void} [props.onAttente] reçoit la liste des
 *   fichiers retenus
 * @returns {import('react').ReactElement} la galerie et ses commandes
 */
export function ArticleMedia({ articleId, medias, onChange, enAttente, onAttente }) {
  const enregistre = estEnregistre(articleId);
  const attente = !enregistre && typeof onAttente === 'function';
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');
  const [ouvert, setOuvert] = useState(false);
  const [grand, setGrand] = useState(null);

  // Les fichiers en attente s'affichent par une adresse locale, rendue au
  // navigateur quand la liste change.
  const locales = useMemo(
    () =>
      (attente ? enAttente || [] : []).map((p, i) => ({
        id: 'attente-' + i,
        rang: i,
        typePiece: p.typePiece,
        nomFichier: p.nom,
        fichier: p.typePiece === 'PLAN' ? '' : URL.createObjectURL(p.fichier),
        local: true,
      })),
    [attente, enAttente],
  );
  useEffect(
    () => () => locales.forEach((m) => m.fichier && URL.revokeObjectURL(m.fichier)),
    [locales],
  );

  if (!enregistre && !attente) {
    return (
      <div style={{ fontSize: 9, color: C.muted, lineHeight: 1.6 }}>
        Enregistre l’article pour lui attacher des photos et des plans.
      </div>
    );
  }

  const pieces = attente ? locales : medias || [];

  const rafraichit = async () => {
    const liste = await stockApi.medias(articleId);
    onChange && onChange(liste);
  };

  const depose = async (evenement, typePiece) => {
    const fichier = evenement.target.files && evenement.target.files[0];
    // Le champ est vidé aussitôt : sans cela, choisir deux fois le même
    // fichier ne déclencherait rien la seconde fois.
    evenement.target.value = '';
    if (!fichier) {
      return;
    }
    setErreur('');
    setEnvoi(true);
    try {
      const reduit = await reduitLImage(fichier, COTE_MAX);
      if (reduit.size > TAILLE_MAX_PIECE) {
        setErreur(
          `Ce fichier pèse ${enMo(reduit.size)} Mo, au-delà des ${enMo(TAILLE_MAX_PIECE)} Mo admis.`,
        );
        return;
      }
      if (attente) {
        onAttente([...(enAttente || []), { fichier: reduit, typePiece, nom: fichier.name }]);
        return;
      }
      await stockApi.deposeMedia(articleId, reduit, typePiece, fichier.name);
      await rafraichit();
    } catch (e) {
      setErreur(e?.message || 'L’envoi a échoué.');
    } finally {
      setEnvoi(false);
    }
  };

  const retire = async (media) => {
    if (media.local) {
      onAttente((enAttente || []).filter((_, i) => i !== media.rang));
      return;
    }
    const quoi = media.typePiece === 'PLAN' ? 'ce plan' : 'cette photo';
    if (!window.confirm(`Retirer ${quoi} ? Le fichier sera effacé.`)) {
      return;
    }
    setErreur('');
    try {
      await stockApi.retireMedia(articleId, media.id);
      await rafraichit();
    } catch (e) {
      setErreur(e?.message || 'Le retrait a échoué.');
    }
  };

  if (pieces.length === 0 && !ouvert) {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOuvert(true)}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setOuvert(true)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 10.5,
          fontWeight: 800,
          color: C.teal,
          cursor: 'pointer',
          background: C.tealL,
          border: '1px solid ' + C.teal + '44',
          borderRadius: 9,
          padding: '9px 13px',
        }}
      >
        📎 Ajouter une photo ou un plan{' '}
        <span style={{ fontWeight: 600, fontSize: 8.5, opacity: 0.75 }}>facultatif</span>
      </div>
    );
  }

  const { photos, plans } = separeLesPieces(pieces);
  const ajout = (couleur, fond, libelle, accept, typePiece) => (
    <label style={{ width: 64, opacity: envoi ? 0.6 : 1 }}>
      <div
        style={{
          ...CELLULE,
          border: '1.5px dashed ' + couleur,
          background: fond,
          color: couleur,
          cursor: envoi ? 'wait' : 'pointer',
        }}
      >
        <div style={{ fontSize: 20, lineHeight: 1 }}>＋</div>
        <div style={{ fontSize: 7, fontWeight: 800 }}>{libelle}</div>
      </div>
      <input
        type="file"
        accept={accept}
        disabled={envoi}
        style={{ display: 'none' }}
        onChange={(e) => depose(e, typePiece)}
      />
    </label>
  );

  return (
    <div>
      <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap' }}>
        {[...photos, ...plans].map((m) => {
          const plan = m.typePiece === 'PLAN';
          const cadre = {
            ...CELLULE,
            overflow: 'hidden',
            border: '1px solid ' + C.border,
            boxShadow: '0 1px 5px rgba(15,22,38,.12)',
            background: '#fff',
            position: 'relative',
          };
          const contenuPlan = (
            <div style={{ color: C.red, fontSize: 20, textAlign: 'center', lineHeight: 1.1 }}>
              📄<div style={{ fontSize: 7, fontWeight: 800 }}>PDF</div>
            </div>
          );
          return (
            <div key={m.id} style={{ width: 64 }}>
              <div style={cadre}>
                {plan ? (
                  m.local ? (
                    <div title={m.nomFichier}>{contenuPlan}</div>
                  ) : (
                    <a
                      href={adresseApi(m.fichier)}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={'📄 ' + (m.nomFichier || 'Plan')}
                      style={{ textDecoration: 'none' }}
                    >
                      {contenuPlan}
                    </a>
                  )
                ) : (
                  <img
                    src={m.local ? m.fichier : adresseApi(m.fichier)}
                    alt={m.nomFichier || 'Photo de l’article'}
                    onClick={() => setGrand(m)}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                )}
                <button
                  type="button"
                  aria-label={plan ? 'Retirer le plan' : 'Retirer la photo'}
                  onClick={(e) => {
                    e.stopPropagation();
                    retire(m);
                  }}
                  style={CROIX}
                >
                  ×
                </button>
              </div>
              <div
                style={{
                  fontSize: 8,
                  color: C.grey,
                  fontWeight: 700,
                  textAlign: 'center',
                  marginTop: 4,
                }}
              >
                {plan ? '✏️ Plan' : '📷 Photo'}
              </div>
            </div>
          );
        })}
        {ajout(C.teal, C.tealL, 'Photo', 'image/*', 'PHOTO')}
        {ajout(C.purple, '#F5EEFE', 'Plan', 'application/pdf,image/*', 'PLAN')}
      </div>
      <div style={{ fontSize: 8.5, color: C.muted, marginTop: 8, lineHeight: 1.5 }}>
        💻 Ordi : choisis un fichier (photo, plan PDF). 📱 Mobile : appareil photo ou galerie.
        Clique une vignette pour l'agrandir.
      </div>
      {envoi && <div style={{ fontSize: 8.5, color: C.muted, marginTop: 6 }}>Envoi en cours…</div>}
      {erreur && (
        <div
          role="alert"
          style={{
            marginTop: 6,
            fontSize: 9,
            color: C.red,
            background: C.red + '15',
            borderRadius: 7,
            padding: '6px 9px',
            lineHeight: 1.5,
          }}
        >
          {erreur}
        </div>
      )}
      {grand && (
        <div
          onClick={() => setGrand(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15,23,42,.93)',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
            paddingTop: 'max(16px, var(--st-haut))',
            paddingBottom: 'max(16px, env(safe-area-inset-bottom, 0px))',
          }}
        >
          <button
            type="button"
            onClick={() => setGrand(null)}
            style={{
              alignSelf: 'flex-end',
              background: 'rgba(255,255,255,.18)',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: '8px 13px',
              fontSize: 12,
              fontWeight: 800,
              cursor: 'pointer',
              marginBottom: 10,
            }}
          >
            ✕ Fermer
          </button>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 8, overflow: 'hidden', maxWidth: '100%' }}
          >
            <img
              src={grand.local ? grand.fichier : adresseApi(grand.fichier)}
              alt=""
              style={{ display: 'block', maxWidth: '100%', maxHeight: '78vh' }}
            />
          </div>
          <div style={{ color: '#e5e7eb', fontSize: 11, marginTop: 10, fontWeight: 700 }}>
            📷 Photo
          </div>
        </div>
      )}
    </div>
  );
}
